import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import {
	useUnlinkStripeMutation,
	useUpdateInvoiceUserCountMutation,
	useWorkspaceStatisticsQuery,
} from '@treams/treams-network';
import { Button, Color, Heading3, Paragraph, tokens } from '@treams/treams-ui';

import { AddStripeBillingConfigurationForm } from './billing/AddStripeBillingConfigurationForm';
import { WorkspaceAdvanced } from './types';

const BillingOptionContainer = styled(Box)`
	background-color: ${(props) => props.theme.colors.white};
	border: 1px solid ${tokens.border.subtle};
	border-radius: 5px;
	align-items: center;
	font-size: 15px;
`;

const StatisticsContainer = styled(Flex)`
	justify-content: space-between;
	gap: 8px;
`;

const StatisticContainer = styled(Flex)`
	position: relative;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border: 1px solid ${tokens.border.subtle};
	border-radius: 5px;
	padding: 4px;
	width: 100%;
`;

const StatisticLabel = styled(Paragraph)`
	font-size: 12px;
	position: absolute;
	top: -14px;
	padding: 0 3px;
	background-color: ${(props) => props.theme.colors.white};
	left: 6px;
`;

const StatisticValue = styled(Paragraph)`
	font-weight: bold;
	font-size: 18px;
`;

function BillingStatus({
	enabled,
	collectionMethod,
}: {
	enabled: boolean;
	collectionMethod: WorkspaceAdvanced['stripeCollectionMethod'];
}) {
	const { t } = useTranslation('', { keyPrefix: 'workspaces.billing' });

	return (
		<>
			<Color color={enabled ? 'green' : 'slateBlue'}>
				{enabled ? t('status.enabled') : t('status.disabled')}
			</Color>
			{enabled && (
				<Color color="slateBlue">
					&nbsp;({collectionMethod === 'send_invoice' ? t('method.invoice') : t('method.automatic')}
					)
				</Color>
			)}
		</>
	);
}

export default function WorkspaceBilling(props: { workspace: WorkspaceAdvanced }) {
	const { t } = useTranslation('', { keyPrefix: 'workspaces.billing' });
	const theme = useTheme();
	const isStripeCustomer = !!props.workspace.stripeCustomerID;
	const minimumUserCount = props.workspace.plans.reduce(
		(total, plan) => total + plan.minimumUserCount,
		0,
	);

	const [verifyUnlink, setVerifyUnlink] = useState(false);

	const { data } = useWorkspaceStatisticsQuery({
		variables: {
			workspaceID: props.workspace.id,
		},
	});

	const activeUsers = data ? data.workspaceStatistics.active + data.workspaceStatistics.draft : 0;
	const minimumIsMoreThanActive = minimumUserCount > activeUsers;

	const [updateInvoiceUserCount, { loading: updateLoading }] = useUpdateInvoiceUserCountMutation();
	const [unlinkStripe, { loading: unlinkLoading }] = useUnlinkStripeMutation();

	const handleSyncUserCount = () => {
		if (!props.workspace.stripeCustomerID) {
			return;
		}

		updateInvoiceUserCount({
			variables: {
				input: {
					workspaceID: props.workspace.id,
					stripeCustomerID: props.workspace.stripeCustomerID,
				},
			},
		});
	};

	const handleUnlinkStripe = () => {
		if (!verifyUnlink) {
			setVerifyUnlink(true);
			return;
		}

		unlinkStripe({
			variables: {
				workspaceID: props.workspace.id,
			},
		});

		window.location.reload();
	};

	return (
		<Box flexGrow={1} flexBasis={0}>
			<Heading3 mb={2}>{t('heading')}</Heading3>

			<BillingOptionContainer p={3}>
				{isStripeCustomer ? (
					<Flex justifyContent="space-between" alignItems="center">
						<Box>
							<Heading3>{t('stripe')}</Heading3>
							<BillingStatus
								enabled={isStripeCustomer}
								collectionMethod={props.workspace.stripeCollectionMethod}
							/>
						</Box>

						<Button
							title={t('manage')}
							small
							type="secondary"
							link={`https://dashboard.stripe.com/customers/${props.workspace.stripeCustomerID}`}
							target="_blank"
						/>
					</Flex>
				) : (
					<AddStripeBillingConfigurationForm workspaceID={props.workspace.id} />
				)}
			</BillingOptionContainer>

			{isStripeCustomer && data?.workspaceStatistics && (
				<BillingOptionContainer p={3}>
					<Flex width="100%" justifyContent="space-between" mb={2}>
						<Box width="100%">
							<Heading3 mb={3}>{t('statistics')}</Heading3>
							<StatisticsContainer justifyContent="space-between" width="100%" mb={3}>
								<StatisticContainer>
									<StatisticLabel>Actief</StatisticLabel>
									<StatisticValue color={theme.colors.green}>
										{data.workspaceStatistics.active}
									</StatisticValue>
								</StatisticContainer>
								<StatisticContainer>
									<StatisticLabel>Concept</StatisticLabel>
									<StatisticValue color={theme.colors.greenDark}>
										{data.workspaceStatistics.draft}
									</StatisticValue>
								</StatisticContainer>
								<StatisticContainer>
									<StatisticLabel>Archief</StatisticLabel>
									<StatisticValue color={theme.colors.yellow}>
										{data.workspaceStatistics.archived}
									</StatisticValue>
								</StatisticContainer>
								<StatisticContainer>
									<StatisticLabel>Inactief</StatisticLabel>
									<StatisticValue color={theme.colors.red}>
										{data.workspaceStatistics.deactivated}
									</StatisticValue>
								</StatisticContainer>
								<StatisticContainer>
									<StatisticLabel>Minimum</StatisticLabel>
									<StatisticValue color={tokens.text.secondary}>{minimumUserCount}</StatisticValue>
								</StatisticContainer>
							</StatisticsContainer>
							<StatisticContainer mb={3}>
								<StatisticLabel>Totaal op invoice</StatisticLabel>
								<StatisticValue color={theme.colors.primary}>
									{minimumIsMoreThanActive ? minimumUserCount : activeUsers}
								</StatisticValue>
							</StatisticContainer>
							<Button
								loading={updateLoading}
								disabled={updateLoading}
								title={t('sync')}
								small
								type="secondary"
								onClick={handleSyncUserCount}
							/>
						</Box>
					</Flex>
				</BillingOptionContainer>
			)}
			{isStripeCustomer && (
				<Button
					loading={unlinkLoading}
					disabled={unlinkLoading}
					disableSuccess
					title={verifyUnlink ? t('verify') : t('unlink')}
					small
					type="secondary"
					onClick={handleUnlinkStripe}
				/>
			)}
		</Box>
	);
}
